import { DEFAULT_PORTRAIT_ASPECT } from '@/lib/imagePrompts';
import { supabase, N8N_BASE_URL } from '@/lib/supabase';
import { fetchMemberAssetsSafe, fetchMemberGenerationJobsSafe, fetchPublicQueueStatus, confirmAssetSafe } from '@/api/memberSafe';
import { safeAssetToBoardView } from '@/lib/member/safeAssets';
import { getUserId } from '@/api/auth';

export interface DbCharacter {
  id: number;
  name: string;
  description?: string | null;
  business_goal?: string | null;
  status?: string | null;
  target_platforms?: string[] | null;
  avatar_url?: string | null;
  voice_id?: string | null;
  heygen_voice_id?: string | null;
  talking_photo_id?: string | null;
  owner_id?: string | null;
  gender?: 'female' | 'male' | string | null;
  visual_variants?: RoleVisualVariants | null;
}

export interface RoleVisualVariants {
  base_style?: string;
  allowed_outfits?: string[];
  allowed_backgrounds?: string[];
  allowed_camera_styles?: string[];
  allowed_expressions?: string[];
}

export interface DbCharacterProfile {
  id: number;
  character_id: number;
  role_positioning: string | null;
  persona_tags: string[] | null;
  target_audience: Record<string, unknown> | null;
  tone: string | null;
  speaking_style: string | null;
  avoid_words: string[] | null;
  content_boundaries: Record<string, unknown> | null;
  business_direction: Record<string, unknown> | null;
  platform_persona_json: Record<string, unknown> | null;
}

// 取角色的人设 profile(一键设定后给用户确认用)
export async function getCharacterProfile(characterId: number): Promise<DbCharacterProfile | null> {
  const { data, error } = await supabase
    .from('character_life_profiles')
    .select('id,character_id,role_positioning,persona_tags,target_audience,tone,speaking_style,avoid_words,content_boundaries,business_direction,platform_persona_json')
    .eq('character_id', characterId)
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// 认领角色拥有权(建立后以登入身分呼叫;只在尚无 owner 时设为自己)
export async function claimCharacterOwnership(characterId: number): Promise<void> {
  const { error } = await supabase.rpc('claim_character_ownership', { p_id: characterId });
  if (error) throw new Error(error.message || '认领角色失败');
}

export interface DbAsset {
  id: number;
  created_at: string;
  character_id: number | null;
  task_id: number | null;
  asset_type: 'image' | 'video' | 'audio';
  url: string | null;
  source: string | null;
  prompt: string | null;
  status: 'generating' | 'pending_review' | 'ready' | 'failed';
  confirmed_at?: string | null;
  meta: Record<string, unknown> | null;
}

export interface DbVoice {
  id: number;
  voice_id: string;
  name: string | null;
  gender: string | null;
  language: string | null;
  preview_url: string | null;
}

export async function getVoices(): Promise<DbVoice[]> {
  const { data, error } = await supabase.from('heygen_voices').select('*').order('id');
  if (error) throw error;
  return data ?? [];
}

export async function bindVoice(characterId: number, voiceId: string, speed = 1, pitch = 0): Promise<{ ok?: boolean }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_bind_voice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, voice_id: voiceId, speed, pitch }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 角色建置精灵:一键设定(名字+平台→AI 自动人设)
export async function autoSetupCharacter(name: string, platforms: string[], gender: 'female' | 'male' = 'female', specialRequirement = ''): Promise<{ ok?: boolean; character_id?: number }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_auto_setup_character`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, platforms, gender, special_requirement: specialRequirement }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 候选头像(呼叫一次得一张;平行呼叫 3 次得 3 张)
export async function generateAvatarCandidate(characterId: number): Promise<{ ok?: boolean; url?: string; asset_id?: number }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_avatar_candidate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 上传授权头贴/半身照(authorized_upload)→ R2 → 设为角色头像。image 为 dataURL 或纯 base64。
export async function uploadAvatar(characterId: number, imageBase64: string, ext: string): Promise<{ ok?: boolean; url?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_upload_avatar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, image_base64: imageBase64, ext, consent: true }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface FaceReferenceUrls {
  front: string;
  left_45: string;
  right_45: string;
}

export interface FaceReferenceValidationResult {
  ok?: boolean;
  identity_lock_ready?: boolean;
  message?: string;
  issues?: string[];
  same_person_confidence?: number;
  front?: Record<string, unknown>;
  left_45?: Record<string, unknown>;
  right_45?: Record<string, unknown>;
}

export async function validateFaceReferences(
  characterId: number,
  refs: FaceReferenceUrls,
): Promise<FaceReferenceValidationResult> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_validate_face_references`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, ...refs }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return json as FaceReferenceValidationResult;
  return json as FaceReferenceValidationResult;
}

export async function saveFaceReferenceImages(
  characterId: number,
  refs: FaceReferenceUrls,
  validation?: FaceReferenceValidationResult | null,
): Promise<void> {
  const { error } = await supabase
    .from('character_identity_profiles')
    .upsert({
      character_id: characterId,
      identity_profile_id: `identity_${characterId}`,
      identity_type: 'authorized_upload',
      identity_lock_level: 'face_body_voice',
      face_lock: {
        enabled: true,
        reference_images: refs,
        face_reference_images: [refs.front, refs.left_45, refs.right_45],
        consistency_rule: '后续生成必须保持同一脸型、五官、年龄感与真人感;只允许更换衣服、背景、动作与光线。',
        quality_checks: {
          front: validation?.front ?? { angle: 'front', status: 'user_confirmed' },
          left_45: validation?.left_45 ?? { angle: 'left_45', status: 'user_confirmed' },
          right_45: validation?.right_45 ?? { angle: 'right_45', status: 'user_confirmed' },
          same_person_confidence: validation?.same_person_confidence ?? null,
        },
        identity_lock_ready: validation?.identity_lock_ready === true,
      },
      body_lock: {
        enabled: true,
        body_ratio: 'natural',
        style_boundary: ['不过度性感', '不网红精修', '不商业棚拍', '不夸张身材'],
      },
      visual_style: {
        camera_style: '手机拍摄、自然、不过度精修',
        polish_level: 'low_to_medium',
        avoid_style: ['AI塑胶感', '过度美颜', '网红精修', '商业棚拍感'],
      },
      updated_at: new Date().toISOString(),
    }, { onConflict: 'character_id' });
  if (error) throw new Error(error.message || '保存脸部参考图失败');
}

// 选定候选头像为正式头像
export async function setAvatar(characterId: number, url: string): Promise<{ ok?: boolean }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_set_avatar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, url }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface AudienceTarget { layer?: string; audience_type?: string; pain_points?: string[]; content_angle?: string }
export interface PlatformRole { platform?: string; platform_task?: string; content_focus?: string[]; lead_path?: string }
export interface PrivateDomainPlan { lead_magnets?: string[]; comment_keywords?: string[]; public_reply_template?: string; private_reply_template?: string; path?: string[]; next_step?: string; no_hard_drainage?: string }
export interface ActiveOutreachPlan { where_to_find_users?: string[]; daily_actions?: string[]; comment_style?: string; dm_rule?: string; comment_example?: string; dm_example?: string }
export interface SuccessMetrics { content_metrics?: string[]; lead_metrics?: string[]; private_domain_metrics?: string[] }
export interface LastWeekReview { effective_content_types?: string[]; failed_tasks?: string[]; platforms_to_strengthen?: string[]; directions_to_reduce?: string[] }
export interface BusinessStrategy {
  what_you_get?: string;
  expected_effect?: string[];
  do_not_this_week?: string[];
  audience_targets?: AudienceTarget[];
  platform_roles?: PlatformRole[];
  private_domain_plan?: PrivateDomainPlan;
  active_outreach_plan?: ActiveOutreachPlan;
  success_metrics?: SuccessMetrics;
  last_week_review?: LastWeekReview;
}

export interface DbWeeklyBoard {
  id: number;
  created_at: string;
  character_id: number;
  weekly_goal: string | null;
  character_state: string | null;
  why_this_week: string | null;
  platform_strategy: Record<string, unknown> | null;
  content_rhythm: Record<string, string> | null;
  business_strategy: BusinessStrategy | null;
  phase: number | null;
  status: string | null;
}

export interface DbTask {
  id: number;
  created_at: string;
  weekly_board_id: number;
  character_id: number | null;
  platform: string;
  task_type: string;
  title: string | null;
  status: string;
  day_index?: number | null;
  published?: boolean;
  downloaded?: boolean;
}

// 用户自行标记任务的「已发布 / 已下载」(本人或管理员)
export async function setTaskFlag(taskId: number, flags: { published?: boolean; downloaded?: boolean }): Promise<void> {
  const { error } = await supabase.rpc('set_task_flag', {
    p_task_id: taskId,
    p_published: flags.published ?? null,
    p_downloaded: flags.downloaded ?? null,
  });
  if (error) throw new Error(error.message || '标记失败');
}

// ===== 后台营运管理(P3):供应商 / 系统设定 / 风控 =====
export interface DbProvider { provider_key: string; provider_name: string; supported_features: string[]; status: string; fallback_provider: string | null; note: string | null }
export async function getProviders(): Promise<DbProvider[]> {
  const { data, error } = await supabase.from('providers').select('*').order('provider_key');
  if (error) return [];
  return (data ?? []) as DbProvider[];
}
export async function adminSetProviderStatus(key: string, status: string): Promise<void> {
  const { error } = await supabase.rpc('admin_set_provider_status', { p_key: key, p_status: status });
  if (error) throw new Error(error.message || '更新失败');
}

export interface DbSystemSetting { key: string; value: unknown; label: string | null }
export async function getSystemSettings(): Promise<DbSystemSetting[]> {
  const { data, error } = await supabase.from('system_settings').select('*').order('key');
  if (error) return [];
  return (data ?? []) as DbSystemSetting[];
}
export async function adminSetSetting(key: string, value: unknown): Promise<void> {
  const { error } = await supabase.rpc('admin_set_setting', { p_key: key, p_value: value });
  if (error) throw new Error(error.message || '更新失败');
}

export interface RiskItem { id: number; task_id: number; title: string | null; risk_level: string | null; risk_issues: string[] | null; created_at: string }
export async function getRiskItems(limit = 80): Promise<RiskItem[]> {
  const { data, error } = await supabase.from('task_outputs').select('id,task_id,title,risk_level,risk_issues,created_at').in('risk_level', ['medium', 'high']).order('id', { ascending: false }).limit(limit);
  if (error) return [];
  return (data ?? []) as RiskItem[];
}

// ===== 后台营运管理(P2):角色/生成任务/素材 明细 =====
export async function adminListMediaJobs(limit = 100): Promise<DbMediaJob[]> {
  const { data, error } = await supabase.from('media_generation_jobs').select('*').order('id', { ascending: false }).limit(limit);
  if (error) return [];
  return (data ?? []) as DbMediaJob[];
}

export async function adminListAssets(limit = 150): Promise<DbAsset[]> {
  const { data, error } = await supabase.from('assets').select('*').order('id', { ascending: false }).limit(limit);
  if (error) return [];
  return (data ?? []) as DbAsset[];
}

// 角色商业状态卡摘要:每个角色最新一周策略的商业重点 + 任务进度,给「我的角色」作战台用
export interface RoleCardSummary {
  character_id: number;
  phase: number;
  has_strategy: boolean;
  weekly_goal: string;
  audiences: string[];
  platform_roles: { platform: string; task: string }[];
  private_domain_path: string;
  outreach: string;
  content_done: number;
  content_total: number;
  next_best_action: string;
}

export async function getRoleCardSummaries(characterIds: number[]): Promise<Record<number, RoleCardSummary>> {
  const out: Record<number, RoleCardSummary> = {};
  if (characterIds.length === 0) return out;
  const { data: boards } = await supabase
    .from('weekly_boards')
    .select('id,character_id,created_at,weekly_goal,business_strategy')
    .in('character_id', characterIds)
    .order('created_at', { ascending: false });
  const latest: Record<number, { id: number; weekly_goal: string | null; business_strategy: BusinessStrategy | null }> = {};
  const phaseCount: Record<number, number> = {};
  for (const b of boards ?? []) {
    const cid = b.character_id as number;
    phaseCount[cid] = (phaseCount[cid] ?? 0) + 1;
    if (!latest[cid]) latest[cid] = { id: b.id as number, weekly_goal: b.weekly_goal, business_strategy: b.business_strategy as BusinessStrategy | null };
  }
  const latestBoardIds = Object.values(latest).map((x) => x.id);
  const tasksByBoard: Record<number, { total: number; done: number }> = {};
  if (latestBoardIds.length) {
    const { data: tasks } = await supabase.from('tasks').select('weekly_board_id,status').in('weekly_board_id', latestBoardIds);
    for (const t of tasks ?? []) {
      const bid = t.weekly_board_id as number;
      const agg = (tasksByBoard[bid] ||= { total: 0, done: 0 });
      agg.total += 1;
      if (t.status === 'output_ready' || t.status === 'approved' || t.status === 'delivered' || t.status === 'published') agg.done += 1;
    }
  }
  for (const cid of characterIds) {
    const lb = latest[cid];
    const bs = lb?.business_strategy || null;
    const agg = lb ? tasksByBoard[lb.id] : undefined;
    const total = agg?.total ?? 0;
    const done = agg?.done ?? 0;
    const hasStrategy = !!lb;
    let next = '先生成本周策略,系统会建立平台分工、受众与任务';
    if (hasStrategy) {
      if (total === 0) next = '本周策略已生成,进入任务板开始生成内容';
      else if (done === 0) next = '先生成第一篇内容,作为本周流量入口';
      else if (done < total) next = `还有 ${total - done} 个内容任务待生成`;
      else next = '本周内容已生成,可下载发布并沉淀私域';
    }
    out[cid] = {
      character_id: cid,
      phase: phaseCount[cid] ?? 0,
      has_strategy: hasStrategy,
      weekly_goal: (bs?.what_you_get || lb?.weekly_goal || '') as string,
      audiences: (bs?.audience_targets || []).map((a) => a.audience_type || '').filter(Boolean).slice(0, 4),
      platform_roles: (bs?.platform_roles || []).map((p) => ({ platform: p.platform || '', task: p.platform_task || '' })).filter((p) => p.platform).slice(0, 6),
      private_domain_path: (bs?.private_domain_plan?.path || []).join(' → '),
      outreach: (bs?.active_outreach_plan?.daily_actions || []).slice(0, 3).join('、'),
      content_done: done,
      content_total: total,
      next_best_action: next,
    };
  }
  return out;
}

// 首页工作台:整体总览 + 本周待办(全部由现有资料聚合,不前端硬写死)
export interface WeeklyTodo {
  id: string;
  character_id: number;
  role_name: string;
  title: string;
  todo_type: 'strategy' | 'content' | 'private_domain';
  priority: 'high' | 'medium' | 'low';
  action_label: string;
}
export interface UserDashboardSummary {
  total_roles: number;
  weekly_tasks_total: number;
  pending_content_tasks: number;
  done_content_tasks: number;
  private_domain_paths: number;
  active_outreach_roles: number;
  todos: WeeklyTodo[];
}

/** 工作台 / 我的角色：不论是否管理员，只看 owner_id 属于自己的角色 */
export function filterCharactersForUser(
  characters: DbCharacter[],
  uid: string | null,
): DbCharacter[] {
  if (!uid) return [];
  return characters.filter((c) => c.owner_id === uid);
}

/** 本人或管理员可管理该角色(生成、绑定、删除等) */
export function canManageCharacter(c: DbCharacter, uid: string | null, admin: boolean): boolean {
  return admin || (!!uid && c.owner_id === uid);
}

export async function getVisibleCharacters(): Promise<DbCharacter[]> {
  const [chars, uid] = await Promise.all([getCharacters(), getUserId()]);
  return filterCharactersForUser(chars, uid);
}

export async function getUserDashboardSummary(): Promise<UserDashboardSummary> {
  const chars = await getVisibleCharacters();
  const sums = chars.length ? await getRoleCardSummaries(chars.map((c) => c.id)) : {};
  let weekly = 0, pending = 0, done = 0, pd = 0, outreach = 0;
  const todos: WeeklyTodo[] = [];
  for (const c of chars) {
    const s = sums[c.id];
    if (!s) {
      todos.push({ id: `strat-${c.id}`, character_id: c.id, role_name: c.name, title: `为「${c.name}」生成本周策略`, todo_type: 'strategy', priority: 'high', action_label: '生成策略' });
      continue;
    }
    weekly += s.content_total;
    done += s.content_done;
    const remain = Math.max(0, s.content_total - s.content_done);
    pending += remain;
    if (s.private_domain_path) pd += 1;
    if (s.outreach) outreach += 1;
    if (!s.has_strategy) {
      todos.push({ id: `strat-${c.id}`, character_id: c.id, role_name: c.name, title: `为「${c.name}」生成本周策略`, todo_type: 'strategy', priority: 'high', action_label: '生成策略' });
    } else if (remain > 0) {
      todos.push({ id: `content-${c.id}`, character_id: c.id, role_name: c.name, title: `「${c.name}」还有 ${remain} 篇内容待生成`, todo_type: 'content', priority: 'high', action_label: '去生成' });
    }
    if (s.has_strategy && !s.private_domain_path) {
      todos.push({ id: `pd-${c.id}`, character_id: c.id, role_name: c.name, title: `「${c.name}」尚未设定私域承接,流量无法沉淀`, todo_type: 'private_domain', priority: 'medium', action_label: '查看策略' });
    }
  }
  todos.sort((a, b) => (a.priority === b.priority ? 0 : a.priority === 'high' ? -1 : 1));
  return { total_roles: chars.length, weekly_tasks_total: weekly, pending_content_tasks: pending, done_content_tasks: done, private_domain_paths: pd, active_outreach_roles: outreach, todos };
}

export async function getCharacters(): Promise<DbCharacter[]> {
  const { data, error } = await supabase
    .from('characters')
    .select('id,name,description,business_goal,status,target_platforms,avatar_url,voice_id,heygen_voice_id,talking_photo_id,owner_id,gender,visual_variants')
    .order('id', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function getCharacter(id: number): Promise<DbCharacter | null> {
  const { data, error } = await supabase
    .from('characters')
    .select('id,name,description,business_goal,status,target_platforms,avatar_url,voice_id,heygen_voice_id,talking_photo_id,owner_id,gender,visual_variants')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getVideoAssets(characterId: number): Promise<DbAsset[]> {
  const rows = await fetchMemberAssetsSafe({
    characterId,
    assetType: 'video',
    statuses: ['ready', 'generating'],
  });
  return rows.map((r) => safeAssetToBoardView(r) as DbAsset);
}

export interface RoleVideoPack {
  id: number;
  created_at: string;
  character_id: number | null;
  task_id: number | null;
  platform: string | null;
  content_goal: string | null;
  content_type: string | null;
  visual_variant: Record<string, unknown> | null;
  script: Record<string, unknown> | null;
  private_domain: Record<string, unknown> | null;
  cover: Record<string, unknown> | null;
  success_metrics: unknown[] | null;
  risk_check: Record<string, unknown> | null;
  variant_image_url: string | null;
  video_asset_id: number | null;
  status: 'brief' | 'image_ready' | 'video_ready' | 'failed' | string;
}

export async function getRoleVideoPacks(characterIds: number[], limit = 30): Promise<RoleVideoPack[]> {
  if (characterIds.length === 0) return [];
  const { data, error } = await supabase
    .from('role_video_packs')
    .select('*')
    .in('character_id', characterIds)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as RoleVideoPack[];
}

export async function requestRoleVideoStrategy(
  characterId: number,
  platform: string,
  contentGoal: string,
  taskId?: number | null,
): Promise<{ ok?: boolean; pack_id?: number; pack?: RoleVideoPack; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_video_strategy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      character_id: characterId,
      platform,
      content_goal: contentGoal,
      task_id: taskId ?? null,
    }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export async function getPendingAssets(characterId: number): Promise<DbAsset[]> {
  const rows = await fetchMemberAssetsSafe({
    characterId,
    statuses: ['pending_review'],
  });
  return rows.map((r) => safeAssetToBoardView(r) as DbAsset);
}

/** 角色相册:用户已确认入库的永久形象图(同一张脸,不同服装/角度) */
export async function getCharacterPhotos(characterId: number): Promise<DbAsset[]> {
  const rows = await fetchMemberAssetsSafe({
    characterId,
    assetType: 'image',
    characterPhotoOnly: true,
    statuses: ['ready'],
  });
  return rows.map((r) => safeAssetToBoardView(r) as DbAsset);
}

export async function confirmAsset(assetId: number): Promise<DbAsset> {
  const found = await confirmAssetSafe(assetId, { statuses: ['ready', 'pending_review'] });
  return safeAssetToBoardView(found) as DbAsset;
}

export async function rejectAsset(assetId: number): Promise<void> {
  const { error } = await supabase.rpc('reject_asset', { p_asset_id: assetId });
  if (error) throw new Error(error.message || '放弃失败');
}

/** 用户确认模特换衣/形象后写入角色相册(永久) */
export async function saveCharacterPhoto(
  characterId: number,
  url: string,
  prompt: string,
  mode: 'wardrobe' | 'avatar' | 'upload' = 'wardrobe',
  referenceUrl?: string,
): Promise<DbAsset> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const user_id = await getUserId();
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_save_character_photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, url, prompt, mode, reference_url: referenceUrl ?? '', user_id }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  const j = await res.json().catch(() => ({})) as { ok?: boolean; asset?: DbAsset; message?: string };
  if (!j.ok || !j.asset) throw new Error(j.message || '存入相册失败');
  return j.asset;
}

export async function getAssetsByTasks(taskIds: number[]): Promise<DbAsset[]> {
  if (taskIds.length === 0) return [];
  const rows = await fetchMemberAssetsSafe({ taskIds });
  return rows.map((r) => safeAssetToBoardView(r) as DbAsset);
}

export async function getAudioAssets(characterId: number): Promise<DbAsset[]> {
  const rows = await fetchMemberAssetsSafe({
    characterId,
    assetType: 'audio',
    statuses: ['ready', 'generating'],
  });
  return rows.map((r) => safeAssetToBoardView(r) as DbAsset);
}

export interface DbMinimaxVoice {
  id: number;
  voice_id: string;
  name: string | null;
  gender: string | null;
  language: string | null;
  tags: string[] | null;
}

export async function getMinimaxVoices(): Promise<DbMinimaxVoice[]> {
  const { data, error } = await supabase.from('minimax_voices').select('*').order('id');
  if (error) throw error;
  return data ?? [];
}

export interface DbPlatformProfile {
  id: number;
  platform: string;
  commercial_positioning: string | null;
  traffic_logic: string | null;
  business_models: string[] | null;
  suitable_role_types: string[] | null;
  unsuitable_role_types: string[] | null;
  risk_rules: string[] | null;
  conversion_method: string | null;
  core_metrics: string[] | null;
}

export interface DbPlatformSchema {
  id: number;
  platform: string;
  asset_type: string;
  asset_type_name: string | null;
  required_fields: string[] | null;
  optional_fields: string[] | null;
  must_not_generate: string[] | null;
  quality_rules: string[] | null;
}

export interface DbPlatformScoring {
  id: number;
  platform: string;
  exposure_weight: string | null;
  engagement_weight: string | null;
  trust_weight: string | null;
  conversion_weight: string | null;
  main_taboos: string[] | null;
}

export async function getPlatformProfiles(): Promise<DbPlatformProfile[]> {
  const { data, error } = await supabase.from('platform_commercial_profiles').select('*').order('id');
  if (error) throw error;
  return data ?? [];
}

export async function getPlatformSchemas(): Promise<DbPlatformSchema[]> {
  const { data, error } = await supabase.from('platform_asset_schemas').select('*').order('id');
  if (error) throw error;
  return data ?? [];
}

export async function getPlatformScoring(): Promise<DbPlatformScoring[]> {
  const { data, error } = await supabase.from('platform_scoring_rules').select('*').order('id');
  if (error) throw error;
  return data ?? [];
}

export interface CreateCharacterInput {
  name: string;
  description?: string;
  target_platforms?: string[];
  business_goal?: string;
}

export async function createCharacter(input: CreateCharacterInput): Promise<{ ok?: boolean; character?: DbCharacter }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_create_character`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 删除角色(管理员)。后端 RPC 会检查 is_admin();相依的策略/任务/素材/配音皆 cascade 一并删除。
// 注意:删除后先前花费的点数不会退回。
export async function deleteCharacter(characterId: number): Promise<void> {
  const { error } = await supabase.rpc('delete_character', { p_id: characterId });
  if (error) {
    if ((error.message || '').includes('not admin')) throw new Error('只有管理员可以删除角色');
    throw new Error(error.message || '删除失败');
  }
}

export async function generateAvatar(characterId: number): Promise<{ ok?: boolean; url?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_generate_avatar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export async function getLatestWeeklyBoard(characterId: number): Promise<DbWeeklyBoard | null> {
  const { data, error } = await supabase
    .from('weekly_boards')
    .select('*')
    .eq('character_id', characterId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// 计算这个角色目前是第几阶段(weekly_boards 笔数;第一次=第一阶段,再生成=第二阶段…)
export async function countWeeklyBoards(characterId: number): Promise<number> {
  const { count, error } = await supabase
    .from('weekly_boards')
    .select('id', { count: 'exact', head: true })
    .eq('character_id', characterId);
  if (error) return 1;
  return count ?? 1;
}

// 计算某任务某动作已生成过几次(内容看 task_outputs;配音/影片看 assets)。
// 规则:第1次扣点,第2次(免费重生一次,不回滚),第3次起再扣点。
export async function getGenCount(taskId: number, action: 'content' | 'voice' | 'video'): Promise<number> {
  if (action === 'content') {
    const { count, error } = await supabase.from('task_outputs').select('id', { count: 'exact', head: true }).eq('task_id', taskId);
    if (error) return 0;
    return count ?? 0;
  }
  const assetType = action === 'voice' ? 'audio' : 'video';
  const rows = await fetchMemberAssetsSafe({ taskIds: [taskId], assetType });
  return rows.length;
}

export async function getTasks(weeklyBoardId: number): Promise<DbTask[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('weekly_board_id', weeklyBoardId)
    .order('id', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export interface LeadMagnetAsset {
  type?: string;
  title?: string;
  sections?: string[];
}

export interface ContentPack {
  opening_hook?: string;
  body?: string;
  ending_hook?: string;
  comment_trigger?: string;
  public_reply?: string;
  private_reply?: string;
  private_domain_cta?: string;
  lead_magnet_asset?: LeadMagnetAsset;
}

export interface DbTaskOutput {
  id: number;
  created_at: string;
  task_id: number;
  content_type: string | null;
  title: string | null;
  body: string | null;
  risk_level: 'low' | 'medium' | 'high' | null;
  risk_issues: string[] | null;
  pack: ContentPack | null;
}

export async function getTaskOutputs(taskIds: number[]): Promise<DbTaskOutput[]> {
  if (taskIds.length === 0) return [];
  const { data, error } = await supabase
    .from('task_outputs')
    .select('*')
    .in('task_id', taskIds)
    .order('id', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function generateContent(taskId: number): Promise<{ ok?: boolean; task_id?: number }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_generate_content`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ task_id: taskId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface GenerateStrategyResult {
  ok?: boolean;
  weekly_board_id?: number;
}

export async function generateStrategy(characterId: number): Promise<GenerateStrategyResult> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_strategy_brain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

export interface VideoResult {
  ok?: boolean;
  started?: boolean;
  pending?: boolean;
  mode?: 'image_video' | 'digital_human';
  url?: string;
  message?: string;
}

// 影片是非同步生成:webhook 可能要等一下才回「started」,甚至 Cloudflare 524 逾时。
// 这里加 45s client 逾时,逾时/错误一律当作「已在背景开始」,前端关掉转圈、靠任务板自动轮询显示结果,
// 避免转圈卡死(背景流程仍会把完成的影片写回 assets/R2)。
async function postVideoWebhook(path: string, body: Record<string, unknown>): Promise<VideoResult> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  try {
    const res = await fetch(`${N8N_BASE_URL}/webhook/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(45000),
    });
    if (!res.ok) return { ok: true, started: true, message: '已送出,背景生成中,完成后会自动出现在影片区。' };
    return (await res.json().catch(() => ({ ok: true, started: true }))) as VideoResult;
  } catch {
    return { ok: true, started: true, message: '已送出,背景生成中(可离开此页),完成后会自动出现在影片区。' };
  }
}

// 模式 B：形象动态影片(Magnific 图生影片)——门槛最低,任何有形象图的角色都能用
export async function generateVideoImage(characterId: number, taskId?: number): Promise<VideoResult> {
  return postVideoWebhook('astraea_video_image', { character_id: characterId, task_id: taskId ?? null });
}

// 模式 A：数字人影片(HeyGen 对口型)——用绑定的脸+声音+脚本
export async function generateVideoDigitalHuman(
  characterId: number,
  taskId: number | null,
  script: string,
): Promise<VideoResult> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_video_digital_human`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, task_id: taskId, script }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 模式 C：MiniMax Hailuo 影片(内地友善,图生影片)
export async function generateVideoMinimax(characterId: number, taskId?: number): Promise<VideoResult> {
  return postVideoWebhook('astraea_video_minimax', { character_id: characterId, task_id: taskId ?? null });
}

// 自然口播影片(HeyGen Photo Avatar / Avatar IV)：头贴 + 角色声音 + 脚本 → 同口型有声影片。
// 来源图建议用「干净正面头肩照(不入镜手)」最自然;首次会建 Photo Avatar 并训练(较久),之后重用。
// Legacy：任务板 MVP 已改走 selfie 链;保留 API 供日后 Photo Avatar 入口复用。
export async function generateVideoHeygen(characterId: number, taskId: number | null, script: string, platform?: string): Promise<VideoResult> {
  return postVideoWebhook('astraea_video_heygen', { character_id: characterId, task_id: taskId, script, platform: platform ?? null });
}

// MVP 自拍口播 Happy Path：avatar → 自拍图 → Voice Engine → HeyGen 对嘴 → R2 → assets(mode=selfie_talk)
export async function generateVideoSelfie(characterId: number, taskId: number | null, script: string): Promise<VideoResult> {
  return postVideoWebhook('astraea_video_selfie', { character_id: characterId, task_id: taskId, script });
}

// MiniMax 中文 TTS 配音(脚本+声音→mp3→R2)
export async function generateVoice(
  characterId: number | null,
  taskId: number | null,
  voiceId: string,
  text: string,
  speed = 1,
  pitch = 0,
): Promise<{ ok?: boolean; url?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_generate_voice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, task_id: taskId, voice_id: voiceId, text, speed, pitch }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 为角色建立 HeyGen 数字人脸(把形象图上传 HeyGen,取得 talking_photo_id)。数字人模式前置步骤。
export async function ensureTalkingPhoto(characterId: number): Promise<{ ok?: boolean; talking_photo_id?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_heygen_talking_photo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// ===== 生成中心(独立生成链:文生图/图生图/文生影片/图生影片)=====
export type MediaJobType =
  | 'text_to_image'
  | 'image_to_image'
  | 'text_to_video'
  | 'image_to_video'
  | 'talking_avatar'
  | 'voice_generation';

export interface DbMediaJob {
  id: number;
  job_id: string | null;
  user_id: string | null;
  job_type: MediaJobType;
  provider: string | null;
  input: Record<string, unknown> | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'timeout';
  output_assets: { type?: string; url?: string; key?: string; mm_task_id?: string } | null;
  cost_points: number;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

export async function getPublicQueueStatus() {
  return fetchPublicQueueStatus();
}

// 读自己的生成中心任务（会员走 safe RPC；管理员请用 adminListMediaJobs）
export type MemberMediaJob = import('@/lib/member/safeAssets').MemberSafeMediaJob;

export async function getMediaJobs(limit = 60): Promise<MemberMediaJob[]> {
  return fetchMemberGenerationJobsSafe(limit);
}

export interface GenResult {
  ok?: boolean;
  started?: boolean;
  job_id?: string;
  url?: string;
  message?: string;
}

async function callGen(path: string, payload: Record<string, unknown>): Promise<GenResult> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const user_id = await getUserId();
  const res = await fetch(`${N8N_BASE_URL}/webhook/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, user_id }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 通用图片上传(图生图/图生影片的输入图)→ 回公开 R2 网址。image 为 dataURL 或纯 base64。
export async function genUpload(imageBase64: string, ext: string): Promise<{ ok?: boolean; url?: string; message?: string }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_gen_upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image_base64: imageBase64, ext }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 文生图(同步,回 url)
export async function genTextToImage(prompt: string, aspectRatio = DEFAULT_PORTRAIT_ASPECT): Promise<GenResult> {
  return callGen('astraea_gen_t2i', { prompt, aspect_ratio: aspectRatio });
}

// 生成中心·图生图：有参考图且描述需同一人→锁脸；否则依文字生成半身人像
export async function genImageToImage(prompt: string, aspectRatio = DEFAULT_PORTRAIT_ASPECT, imageUrl?: string | null): Promise<GenResult> {
  const body: Record<string, string> = { prompt, aspect_ratio: aspectRatio };
  if (imageUrl) body.image_url = imageUrl;
  return callGen('astraea_gen_i2i', body);
}

// 任务板·模特换衣：锁脸换装预览
export async function genWardrobeLockface(prompt: string, imageUrl: string, aspectRatio = DEFAULT_PORTRAIT_ASPECT): Promise<GenResult> {
  return callGen('astraea_wardrobe_lockface', { prompt, image_url: imageUrl, aspect_ratio: aspectRatio });
}

// 文生影片(非同步,回 started + job_id;完成后轮询 getMediaJobs)
export async function genTextToVideo(prompt: string): Promise<GenResult> {
  return callGen('astraea_gen_t2v', { prompt });
}

// 图生影片(非同步,回 started + job_id)
export async function genImageToVideo(prompt: string, imageUrl: string): Promise<GenResult> {
  return callGen('astraea_gen_i2v', { prompt, image_url: imageUrl });
}

// 图生口播视频(非同步):上传一张脸的图 + 脚本 + 中文声音(可调语速/语调)→ 对口型有声影片。不绑定角色。
export async function genTalking(imageUrl: string, script: string, voiceId: string, speed = 1, pitch = 0): Promise<GenResult> {
  return callGen('astraea_gen_talking', { image_url: imageUrl, script, voice_id: voiceId, speed, pitch });
}

// ===== 角色落地与开发指令(role_playbooks,角色级,与每周策略解耦)=====
export interface RoleVoiceStyle { personality?: string; background?: string; local_tone?: string; speaking_rules?: string[]; avoid_words?: string[] }
export interface PinnedContent { title?: string; purpose?: string }
export interface PlatformProfileSetup { display_name_options?: string[]; recommended_name?: string; bio?: string; profile_tags?: string[]; homepage_info?: string[]; pinned_content?: PinnedContent[]; profile_warning?: string[] }
export interface TargetUserType { type?: string; signals?: string[]; priority?: string }
export interface WhereToFind { platform?: string; keywords?: string[]; action?: string }
export interface ActiveOutreachPlaybook { target_user_types?: TargetUserType[]; where_to_find?: WhereToFind[]; daily_outreach_tasks?: string[]; do_not_do?: string[] }
export interface CommentScenario { scenario?: string; reply?: string; next_step?: string }
export interface CommentPlaybook { comment_scenarios?: CommentScenario[]; comment_rules?: string[] }
export interface DmOpener { scenario?: string; message?: string; follow_up?: string }
export interface DmPlaybook { when_to_dm?: string[]; when_not_to_dm?: string[]; dm_openers?: DmOpener[]; lead_flow?: string[]; forbidden_dm?: string[] }
export interface RolePlaybookData {
  role_voice_style?: RoleVoiceStyle;
  platform_profile_setup?: Record<string, PlatformProfileSetup>;
  active_outreach_playbook?: ActiveOutreachPlaybook;
  comment_playbook?: CommentPlaybook;
  dm_playbook?: DmPlaybook;
}
export interface DbRolePlaybook { character_id: number; data: RolePlaybookData | null; updated_at: string }

// ===== 任务验收回填(task_metric_results,P0.3 写 DB,取代 localStorage)=====
export interface DbTaskMetricResult {
  id: number;
  task_id: number;
  board_id: number | null;
  role_id: number | null;
  user_id: string | null;
  platform: string | null;
  metric_key: string;
  metric_label: string | null;
  metric_value: string | null;
  metric_unit: string | null;
  submitted_at: string;
}

export async function getTaskMetricResults(boardId: number): Promise<DbTaskMetricResult[]> {
  const { data, error } = await supabase.from('task_metric_results').select('*').eq('board_id', boardId);
  if (error) return [];
  return (data ?? []) as DbTaskMetricResult[];
}

export interface TaskMetricInput { task_id: number; board_id: number; role_id: number; platform: string; metric_key: string; metric_label?: string; metric_value: string; metric_unit?: string }
export async function saveTaskMetricResults(rows: TaskMetricInput[]): Promise<void> {
  if (rows.length === 0) return;
  const user_id = await getUserId();
  const now = new Date().toISOString();
  const payload = rows.map((r) => ({ ...r, user_id, submitted_at: now, updated_at: now }));
  const { error } = await supabase.from('task_metric_results').upsert(payload, { onConflict: 'task_id,metric_key' });
  if (error) throw new Error(error.message || '保存失败');
}

export async function getRolePlaybook(characterId: number): Promise<DbRolePlaybook | null> {
  const { data, error } = await supabase.from('role_playbooks').select('*').eq('character_id', characterId).maybeSingle();
  if (error) return null;
  return data as DbRolePlaybook | null;
}

// ===== 角色 HeyGen 影片声音(可选 + 试听)=====
export interface DbHeygenVoice { voice_id: string; name: string; gender: string; language: string; preview_audio_url: string }
export async function getHeygenVoices(gender = ''): Promise<DbHeygenVoice[]> {
  if (!N8N_BASE_URL) return [];
  try {
    const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_heygen_voices`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gender }),
    });
    if (!res.ok) return [];
    const d = await res.json().catch(() => ({}));
    return (d.voices ?? []) as DbHeygenVoice[];
  } catch { return []; }
}
export async function setHeygenVoice(characterId: number, voiceId: string): Promise<void> {
  const { error } = await supabase.rpc('set_heygen_voice', { p_character_id: characterId, p_voice_id: voiceId });
  if (error) throw new Error(error.message || '设定声音失败');
}

// ===== AI 分镜短影音(Storyboard → HeyGen Video Agent)=====
export interface StoryboardShot { scene?: string; narration?: string }
export interface Storyboard { ok?: boolean; title?: string; shots?: StoryboardShot[]; video_prompt?: string; est_seconds?: number; max_seconds?: number; character_id?: number }

// 生成分镜草稿(qwen,便宜·不花 HeyGen 钱):给用户先确认,满意才渲染。约 30-90s。
export async function generateStoryboard(characterId: number, requirement = '', maxSeconds = 30): Promise<Storyboard> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_storyboard`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId, requirement, max_seconds: maxSeconds }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}

// 确认后渲染:HeyGen Video Agent(锁脸+角色声音+分镜 prompt → 多场景长片)。非同步,完成自动写回 assets。
export async function generateVideoAgent(characterId: number, taskId: number | null, prompt: string, maxSeconds = 30): Promise<VideoResult> {
  return postVideoWebhook('astraea_video_agent', { character_id: characterId, task_id: taskId, prompt, max_seconds: maxSeconds });
}

// 触发角色开发指令生成(n8n: 读角色+profile → AI 产五区块 → upsert role_playbooks)。约 60-100s。
export async function generateRolePlaybook(characterId: number): Promise<{ ok?: boolean }> {
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_role_playbook`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ character_id: characterId }),
  });
  if (!res.ok) throw new Error(`服务暂时无法连线(${res.status}),请稍后再试`);
  return res.json().catch(() => ({}));
}
